import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import { BryntumSchedulerPro } from '@bryntum/schedulerpro-react';
import { SchedulerContextValue } from '../types/scheduler.types';

const SchedulerContext = createContext<SchedulerContextValue | null>(null);

interface SchedulerProviderProps {
  children: ReactNode;
  initialTimeZone?: string;
  isMapOpen?: boolean;
}

export function SchedulerProvider({
  children,
  initialTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  isMapOpen = false,
}: SchedulerProviderProps) {
  const schedulerRef = useRef<BryntumSchedulerPro>(null);
  const [timeZone, setTimeZone] = useState(initialTimeZone);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Undo action
  const undo = useCallback(() => {
    const scheduler = schedulerRef.current?.instance;
    if (scheduler?.project?.stm?.canUndo) {
      scheduler.project.stm.undo();
      setCanUndo(scheduler.project.stm.canUndo);
      setCanRedo(scheduler.project.stm.canRedo);
    }
  }, []);

  // Redo action
  const redo = useCallback(() => {
    const scheduler = schedulerRef.current?.instance;
    if (scheduler?.project?.stm?.canRedo) {
      scheduler.project.stm.redo();
      setCanUndo(scheduler.project.stm.canUndo);
      setCanRedo(scheduler.project.stm.canRedo);
    }
  }, []);

  // Update can undo/redo state
  const updateUndoRedoState = useCallback(() => {
    const scheduler = schedulerRef.current?.instance;
    if (scheduler?.project?.stm) {
      setCanUndo(scheduler.project.stm.canUndo);
      setCanRedo(scheduler.project.stm.canRedo);
    }
  }, []);

  // Shift handlers - set by SchedulerCore which has access to both scheduler and onDateRangeChange
  // Initial no-op functions are replaced by registerShiftHandlers when SchedulerCore mounts
  const shiftHandlersRef = useRef<{
    shiftNext: () => void;
    shiftPrevious: () => void;
    shiftToday: () => void;
  }>({
    shiftNext: () => undefined,
    shiftPrevious: () => undefined,
    shiftToday: () => undefined,
  });

  const registerShiftHandlers = useCallback(
    (handlers: {
      shiftNext: () => void;
      shiftPrevious: () => void;
      shiftToday: () => void;
    }) => {
      shiftHandlersRef.current = handlers;
    },
    []
  );

  const shiftNext = useCallback(() => {
    shiftHandlersRef.current.shiftNext();
  }, []);

  const shiftPrevious = useCallback(() => {
    shiftHandlersRef.current.shiftPrevious();
  }, []);

  const shiftToday = useCallback(() => {
    shiftHandlersRef.current.shiftToday();
  }, []);

  const value = useMemo<SchedulerContextValue>(
    () => ({
      schedulerRef,
      timeZone,
      setTimeZone,
      canUndo,
      canRedo,
      undo,
      redo,
      updateUndoRedoState,
      shiftNext,
      shiftPrevious,
      shiftToday,
      registerShiftHandlers,
      isMapOpen,
    }),
    [
      timeZone,
      canUndo,
      canRedo,
      undo,
      redo,
      updateUndoRedoState,
      shiftNext,
      shiftPrevious,
      shiftToday,
      registerShiftHandlers,
      isMapOpen,
    ]
  );

  return (
    <SchedulerContext.Provider value={value}>
      {children}
    </SchedulerContext.Provider>
  );
}

export function useSchedulerContext(): SchedulerContextValue {
  const context = useContext(SchedulerContext);
  if (!context) {
    throw new Error(
      'useSchedulerContext must be used within a SchedulerProvider'
    );
  }
  return context;
}

export { SchedulerContext };
